/* eslint-disable react/no-unused-state */
import React, { Component } from "react";

import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CRow,
  CCol,
  CSwitch,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CTooltip
} from "@coreui/react";
import {
  CircleCheck,
  ChartColumn,
  Layers,
  BookOpen,
  GraduationCap,
  MessageSquare,
  PenLine,
  Save,
  X,
  Trash2,
  BookMarked,
  Target
} from "lucide-react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { bindActionCreators } from "redux";

import { logout } from "../../../../../../actions/baseActions";
import { setObj } from "../../../../../../actions/noteActions";
import Breadcrumb from "../../../../../../components/Breadcrumb";
import Loading from "../../../../../../components/Loading";
import ProtectedRoute from "../../../../../../components/ProtectedRoute";
import ApplicatedQuestionVisualization from "../../../../../../components/QuestionBank/QuestionVisualization/QuestionVisualization";
import TextEditor from "../../../../../../components/TextEditor/TextEditor";
import URL from "../../../../../../config/api";
import axios from "../../../../../../config/axios";

import "./styles.css";

class QuestionBankReviewEdit extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      question: null,
      questionId: null,
      editMode: false,
      editedDescription: null,
      showCommentInput: false,
      commentText: "",
      comments: [],
      loadingComments: false,
      editingCommentId: null,
      editingCommentText: "",
      showDeleteModal: false,
      commentToDelete: null,
      visualizationKey: 0
    };
  }

  componentDidMount() {
    const { match } = this.props;
    const questionId = match?.params?.id;

    if (!questionId) {
      toastr.error("Erro", "ID da questão não encontrado");
      this.props.history.push("/evaluation/question/bank/review/list");
      return;
    }

    this.setState({ questionId }, () => {
      this.loadQuestion();
      this.loadComments();
    });
  }

  componentWillUnmount() {
    // Limpar localStorage se estiver saindo para outra página que não seja review/list
    const currentPath = window.location.pathname;
    if (
      !currentPath.includes("/question/bank/review/list") &&
      !currentPath.includes("/question/bank/review/edit")
    ) {
      localStorage.removeItem("questionBankReviewFilters");
    }
  }

  loadQuestion = async () => {
    const { questionId } = this.state;

    try {
      const response = await axios.get(
        `${URL.api}evaluation/question/singleView/${questionId}`,
        {
          headers: { Authorization: `Bearer ${this.props.user.token}` }
        }
      );

      if (response.data.success) {
        const questionData = response.data.data;
        // Decodificar Unicode escapado que vem do backend
        if (questionData.description) {
          questionData.description = this.unescapeUnicode(
            questionData.description
          );
        }
        if (questionData.feedback) {
          questionData.feedback = this.unescapeUnicode(questionData.feedback);
        }
        if (questionData.options) {
          questionData.options = questionData.options.map(opt => ({
            ...opt,
            description: this.unescapeUnicode(opt.description),
            feedback: opt.feedback
              ? this.unescapeUnicode(opt.feedback)
              : opt.feedback
          }));
        }
        this.setState({
          question: questionData,
          loading: false
        });
      } else {
        toastr.error("Erro", "Não foi possível carregar a questão");
        this.props.history.push("/evaluation/question/bank/review/list");
      }
    } catch (error) {
      toastr.error("Erro", "Falha ao buscar questão");
      this.props.history.push("/evaluation/question/bank/review/list");
    }
  };

  loadComments = async () => {
    const { questionId } = this.state;
    this.setState({ loadingComments: true });

    try {
      const response = await axios.get(
        `${URL.api}evaluation/question/${questionId}/comments`,
        {
          headers: { Authorization: `Bearer ${this.props.user.token}` }
        }
      );

      if (response.data.success) {
        this.setState({
          comments: response.data.data,
          loadingComments: false
        });
      } else {
        this.setState({ loadingComments: false });
      }
    } catch (error) {
      this.setState({ loadingComments: false });
    }
  };

  getStatusBadge = status => {
    const statusMap = {
      pending: { label: "📋 Revisão", color: "review-yellow" },
      approved: { label: "✅ Aprovado", color: "review-green" },
      rejected: { label: "❌ Rejeitado", color: "review-red" }
    };
    return statusMap[status] || statusMap.pending;
  };

  getDifficultyLabel = difficultyId => {
    const difficultyMap = { 1: "Fácil", 2: "Médio", 3: "Difícil" };
    return difficultyMap[difficultyId] || "Não definido";
  };

  getTypeLabel = typeId => {
    const typeMap = {
      1: "Discursiva",
      2: "Múltipla Escolha",
      3: "Múltipla Resposta",
      4: "Verdadeiro ou Falso"
    };
    return typeMap[typeId] || "Não definido";
  };

  truncateText = (text, maxLength = 20) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  getAbilityDisplay = () => {
    const { question } = this.state;
    if (!question) return "Não definido";

    const abilityText = question.ability_code
      ? `${question.ability_code} - ${question.ability_name}`
      : question.ability_name;

    return abilityText || "Não definido";
  };

  canEditComments = () => {
    const roleId = this.props.general?.active_roles?.role_id;
    // Apenas Administrador (1) e Revisor (10) podem editar/excluir comentários
    return roleId === 1 || roleId === 10;
  };

  toggleEditMode = () => {
    this.setState(prevState => ({ editMode: !prevState.editMode }));
  };

  handleDescriptionChange = newValue => {
    this.setState({ editedDescription: newValue });
  };

  sanitizeSlateContent = content => {
    // Função para limpar o conteúdo do Slate removendo text: null
    try {
      const parsed =
        typeof content === "string" ? JSON.parse(content) : content;

      const sanitize = node => {
        if (Array.isArray(node)) {
          return node.map(sanitize).filter(Boolean);
        }

        if (typeof node === "object" && node !== null) {
          const sanitized = { ...node };

          // Se tem children, sanitiza recursively
          if (sanitized.children) {
            sanitized.children = sanitize(sanitized.children);
          }

          // Se é um nó de texto com text null ou undefined, transforma em string vazia
          if (
            "text" in sanitized &&
            (sanitized.text === null || sanitized.text === undefined)
          ) {
            sanitized.text = "";
          }

          return sanitized;
        }

        return node;
      };

      const sanitized = sanitize(parsed);
      // Sempre retornar como objeto, a API vai fazer o stringify quando necessário
      return sanitized;
    } catch (e) {
      return typeof e === "string" ? content : content;
    }
  };

  unescapeUnicode = str => {
    // Função para decodificar caracteres Unicode escapados (\uXXXX)
    if (typeof str !== "string") return str;
    return str.replace(/\\u([0-9a-fA-F]{4})/g, (match, grp) =>
      String.fromCharCode(parseInt(grp, 16))
    );
  };

  handleSaveEdit = async () => {
    const { question, editedDescription } = this.state;

    // Verificar se a questão está sendo utilizada
    if (question.released) {
      toastr.warning(
        "Atenção",
        "Essa questão está em uma prova já aplicada e não é possível editá-la."
      );
      return;
    }

    if (!editedDescription) {
      toastr.warning("Atenção", "Nenhuma alteração foi feita");
      return;
    }

    // editedDescription vem do TextEditor como objeto, precisa ser stringificado primeiro
    const descriptionString =
      typeof editedDescription === "string"
        ? editedDescription
        : JSON.stringify(editedDescription);

    // Sanitizar o conteúdo para evitar erros do Slate
    const sanitizedDescription = this.sanitizeSlateContent(descriptionString);

    // Preparar as opções no formato esperado pela API
    const itemsToSend = question.options.map(item => ({
      id: item.id,
      description: item.description,
      check_value: item.check_value,
      feedback: item.feedback,
      father_option_id: item.father_option_id
    }));

    // Preparar objeto com todos os campos, alterando apenas o description
    const obj = {
      id: question.id,
      description:
        typeof sanitizedDescription === "string"
          ? sanitizedDescription
          : JSON.stringify(sanitizedDescription),
      feedback: question.feedback,
      grade: question.grade,
      author: question.author,
      module_id: question.module_id,
      ability_id: question.ability_id,
      objective_id: question.objective_id,
      L_id: 1,
      difficulty_level_id: question.difficulty_level_id,
      type_question_id: question.type_question_id,
      user_id: question.user_id,
      source_id: 1,
      released: question.released,
      private: question.private,
      segment_id: question.segment_id,
      discipline_id: question.discipline_id,
      canceled: false,
      options: itemsToSend
    };

    try {
      const response = await axios.post(
        `${URL.api}evaluation/question/updateReview`,
        obj,
        {
          headers: { Authorization: `Bearer ${this.props.user.token}` }
        }
      );

      if (response.data.success) {
        toastr.success("Sucesso", "Enunciado atualizado com sucesso");
        this.setState(
          prevState => ({
            editMode: false,
            editedDescription: null,
            visualizationKey: prevState.visualizationKey + 1
          }),
          () => {
            this.loadQuestion(); // Recarrega a questão atualizada
          }
        );
      } else {
        toastr.error(
          "Erro",
          response.data.error || "Não foi possível salvar a edição"
        );
        // Ocultar botões de edição quando há erro
        this.handleCancelEdit();
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Falha ao salvar edição";
      toastr.error("Erro", errorMessage);
      // Ocultar botões de edição quando há erro
      this.handleCancelEdit();
    }
  };

  handleCancelEdit = () => {
    this.setState({ editMode: false, editedDescription: null });
  };

  handleShowCommentInput = () => {
    this.setState({ showCommentInput: true });
  };

  handleCancelComment = () => {
    this.setState({ showCommentInput: false, commentText: "" });
  };

  handleSaveComment = async () => {
    const { commentText, questionId } = this.state;
    if (!commentText.trim()) {
      toastr.warning("Atenção", "Digite um comentário antes de salvar");
      return;
    }

    try {
      const response = await axios.post(
        `${URL.api}evaluation/question/${questionId}/comments`,
        { description: commentText },
        {
          headers: { Authorization: `Bearer ${this.props.user.token}` }
        }
      );

      if (response.data.success) {
        toastr.success("Sucesso", "Comentário adicionado com sucesso");
        this.setState({ showCommentInput: false, commentText: "" }, () => {
          this.loadComments();
        });
      } else {
        toastr.error("Erro", "Não foi possível adicionar o comentário");
      }
    } catch (error) {
      toastr.error("Erro", "Falha ao adicionar comentário");
    }
  };

  handleEditComment = comment => {
    if (!this.canEditComments()) {
      toastr.warning(
        "Permissão negada",
        "Apenas Administradores e Revisores podem editar comentários."
      );
      return;
    }
    this.setState({
      editingCommentId: comment.id,
      editingCommentText: comment.description
    });
  };

  handleCancelEditComment = () => {
    this.setState({
      editingCommentId: null,
      editingCommentText: ""
    });
  };

  handleSaveEditComment = async commentId => {
    if (!this.canEditComments()) {
      toastr.warning(
        "Permissão negada",
        "Apenas Administradores e Revisores podem editar comentários."
      );
      return;
    }

    const { editingCommentText, questionId } = this.state;
    if (!editingCommentText.trim()) {
      toastr.warning("Atenção", "O comentário não pode estar vazio");
      return;
    }

    try {
      const response = await axios.put(
        `${URL.api}evaluation/question/${questionId}/comments/${commentId}`,
        { description: editingCommentText },
        {
          headers: { Authorization: `Bearer ${this.props.user.token}` }
        }
      );

      if (response.data.success) {
        toastr.success("Sucesso", "Comentário atualizado com sucesso");
        this.setState(
          {
            editingCommentId: null,
            editingCommentText: ""
          },
          () => {
            this.loadComments();
          }
        );
      } else {
        toastr.error("Erro", response.data.error);
      }
    } catch (error) {
      toastr.error("Erro", "Falha ao atualizar comentário");
    }
  };

  handleDeleteComment = commentId => {
    if (!this.canEditComments()) {
      toastr.warning(
        "Permissão negada",
        "Apenas Administradores e Revisores podem excluir comentários."
      );
      return;
    }
    this.setState({
      showDeleteModal: true,
      commentToDelete: commentId
    });
  };

  handleCancelDelete = () => {
    this.setState({
      showDeleteModal: false,
      commentToDelete: null
    });
  };

  handleConfirmDelete = async () => {
    const { questionId, commentToDelete } = this.state;

    this.setState({ showDeleteModal: false });

    try {
      const response = await axios.delete(
        `${URL.api}evaluation/question/${questionId}/comments/${commentToDelete}`,
        {
          headers: { Authorization: `Bearer ${this.props.user.token}` }
        }
      );

      if (response.data.success) {
        toastr.success("Sucesso", "Comentário excluído com sucesso");
        this.setState({ commentToDelete: null });
        this.loadComments();
      } else {
        toastr.error("Erro", response.data.error);
      }
    } catch (error) {
      toastr.error("Erro", "Falha ao excluir comentário");
    }
  };

  handleCommentChange = e => {
    this.setState({ commentText: e.target.value });
  };

  handleEditCommentTextChange = e => {
    this.setState({ editingCommentText: e.target.value });
  };

  formatDate = dateString => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  getDescriptionForEditor = () => {
    const { editedDescription, question } = this.state;

    if (editedDescription) {
      return typeof editedDescription === "string"
        ? editedDescription
        : JSON.stringify(editedDescription);
    }

    if (question.description) {
      // Se já é uma string JSON, retorna direto
      if (typeof question.description === "string") {
        try {
          JSON.parse(question.description);
          return question.description;
        } catch (e) {
          // Se não é JSON válido, cria estrutura padrão com o texto
          return JSON.stringify([
            { type: "paragraph", children: [{ text: question.description }] }
          ]);
        }
      }
      // Se é objeto, faz stringify
      return JSON.stringify(question.description);
    }

    // Valor padrão
    return JSON.stringify([{ type: "paragraph", children: [{ text: "" }] }]);
  };

  render() {
    const {
      loading,
      question,
      editMode,
      editedDescription,
      showCommentInput,
      comments,
      loadingComments,
      editingCommentId,
      editingCommentText
    } = this.state;

    if (loading) {
      return (
        <>
          <Breadcrumb
            current={this.props.currentRoute}
            items={[
              {
                name: "Banco de Questões - Revisão",
                to: "/evaluation/question/bank/review/list"
              }
            ]}
          />
          <div className="review-edit-container">
            <Loading />
          </div>
          <ProtectedRoute routeId={this.props.currentRoute} />
        </>
      );
    }

    if (!question) {
      return (
        <>
          <Breadcrumb
            current={this.props.currentRoute}
            items={[
              {
                name: "Banco de Questões - Revisão",
                to: "/evaluation/question/bank/review/list"
              }
            ]}
          />
          <div className="review-edit-container">
            <p>Questão não encontrada</p>
          </div>
          <ProtectedRoute routeId={this.props.currentRoute} />
        </>
      );
    }

    return (
      <>
        <Breadcrumb
          current={this.props.currentRoute}
          items={[
            {
              name: "Banco de Questões - Revisão",
              to: "/question/bank/review/list"
            }
          ]}
        />

        <CButton
          color="secondary"
          size="sm"
          className="mb-3"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            width: "fit-content"
          }}
          onClick={() => this.props.history.push("/question/bank/review/list")}
        >
          <i className="fa fa-arrow-left" /> Voltar para lista
        </CButton>

        <CCard>
          <CCardHeader
            style={{
              backgroundColor: editMode ? "#12517D" : "#2EA8D8",
              color: "white"
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div>
                  <h4 className="card-title mb-1" style={{ color: "white" }}>
                    {editMode ? "Edição de Questão" : "Revisão de Questão"}
                  </h4>
                  <div className="small" style={{ color: "white" }}>
                    ID: {question.id}
                  </div>
                </div>
              </div>
              <div className="mode-switch-container">
                <span
                  className={`mode-label ${!editMode ? "active" : ""}`}
                  style={{ color: "white" }}
                >
                  Revisão
                </span>
                <CSwitch
                  color="primary"
                  variant="pill"
                  checked={editMode}
                  onChange={() => this.toggleEditMode()}
                />
                <span
                  className={`mode-label ${editMode ? "active" : ""}`}
                  style={{ color: "white" }}
                >
                  Edição
                </span>
              </div>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow className="g-3">
              <CCol lg="6">
                <CCard>
                  <CCardHeader>
                    <h5 className="mb-0">Informações da Questão</h5>
                  </CCardHeader>
                  <CCardBody>
                    <div className="info-grid">
                      <div className="info-item">
                        <ChartColumn className="info-icon" />
                        <div className="info-content">
                          <p className="info-label">Dificuldade</p>
                          <p className="info-value">
                            {this.getDifficultyLabel(
                              question.difficulty_level_id
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="info-item">
                        <Layers className="info-icon" />
                        <div className="info-content">
                          <p className="info-label">Tipo</p>
                          <p className="info-value">
                            {this.getTypeLabel(question.type_question_id)}
                          </p>
                        </div>
                      </div>
                      <div className="info-item">
                        <BookOpen className="info-icon" />
                        <div className="info-content">
                          <p className="info-label">Componente Curricular</p>
                          <p className="info-value">
                            {question.discipline_name || "Não definido"}
                          </p>
                        </div>
                      </div>
                      <div className="info-item">
                        <GraduationCap className="info-icon" />
                        <div className="info-content">
                          <p className="info-label">Segmento</p>
                          <p className="info-value">
                            {question.segment_name || "Não definido"}
                          </p>
                        </div>
                      </div>
                      <div className="info-item">
                        <BookMarked className="info-icon" />
                        <div className="info-content">
                          <p className="info-label">Módulo</p>
                          <p className="info-value">
                            {question.module_name || "Não definido"}
                          </p>
                        </div>
                      </div>
                      <div className="info-item">
                        <Target className="info-icon" />
                        <div className="info-content">
                          <p className="info-label">Habilidade</p>
                          <CTooltip
                            content={this.getAbilityDisplay()}
                            placement="top"
                          >
                            <p className="info-value">
                              {this.truncateText(this.getAbilityDisplay(), 30)}
                            </p>
                          </CTooltip>
                        </div>
                      </div>
                    </div>
                  </CCardBody>
                </CCard>

                {editMode && (
                  <CCard className="mt-3">
                    <CCardHeader>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Editar Enunciado</h5>
                      </div>
                    </CCardHeader>
                    <CCardBody>
                      <div className="text-editor-wrapper">
                        <TextEditor
                          editorType={{ id: 1 }}
                          textContent={this.getDescriptionForEditor()}
                          changeQuestionDescription={
                            this.handleDescriptionChange
                          }
                          readOnly={false}
                          questionId={question.id}
                        />
                      </div>
                      <div className="edit-buttons-container">
                        <CButton color="primary" onClick={this.handleSaveEdit}>
                          <Save size={16} /> Salvar Edição
                        </CButton>
                        <CButton
                          color="secondary"
                          variant="outline"
                          onClick={this.handleCancelEdit}
                        >
                          <X size={16} /> Cancelar
                        </CButton>
                      </div>
                    </CCardBody>
                  </CCard>
                )}

                {!editMode && (
                  <>
                    <CCard className="mt-3">
                      <CCardHeader>
                        <h5 className="mb-0">Comentários</h5>
                      </CCardHeader>
                      <CCardBody>
                        {loadingComments ? (
                          <div className="text-center py-3">
                            <Loading />
                          </div>
                        ) : (
                          <>
                            {comments.length > 0 ? (
                              <div className="comments-list mb-3">
                                {comments.map(comment => (
                                  <div
                                    key={comment.id}
                                    className="comment-item"
                                  >
                                    {editingCommentId === comment.id ? (
                                      <div className="edit-comment-container">
                                        <textarea
                                          className="form-control mb-2"
                                          rows="3"
                                          value={editingCommentText}
                                          onChange={
                                            this.handleEditCommentTextChange
                                          }
                                        />
                                        <div className="d-flex justify-content-end gap-2">
                                          <CButton
                                            color="secondary"
                                            variant="outline"
                                            size="sm"
                                            onClick={
                                              this.handleCancelEditComment
                                            }
                                          >
                                            Cancelar
                                          </CButton>
                                          <CButton
                                            color="primary"
                                            size="sm"
                                            onClick={() =>
                                              this.handleSaveEditComment(
                                                comment.id
                                              )
                                            }
                                          >
                                            Salvar
                                          </CButton>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <div className="comment-header">
                                          <div className="comment-author-info">
                                            <strong>
                                              {comment.author_name}
                                            </strong>
                                            <span className="comment-date">
                                              {" · "}
                                              {this.formatDate(
                                                comment.created_at
                                              )}
                                            </span>
                                          </div>
                                          {this.canEditComments() && (
                                            <div className="comment-actions">
                                              <button
                                                type="button"
                                                className="comment-action-btn edit-btn"
                                                onClick={() =>
                                                  this.handleEditComment(
                                                    comment
                                                  )
                                                }
                                                title="Editar comentário"
                                              >
                                                <PenLine size={16} />
                                              </button>
                                              <button
                                                type="button"
                                                className="comment-action-btn delete-btn"
                                                onClick={() =>
                                                  this.handleDeleteComment(
                                                    comment.id
                                                  )
                                                }
                                                title="Excluir comentário"
                                              >
                                                <Trash2 size={16} />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                        <div className="comment-body">
                                          {comment.description}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center text-muted py-3 mb-3">
                                <p className="mb-0">Nenhum comentário ainda.</p>
                              </div>
                            )}

                            {!showCommentInput ? (
                              <CButton
                                color="secondary"
                                variant="outline"
                                className="w-100"
                                style={{ borderStyle: "dashed" }}
                                onClick={this.handleShowCommentInput}
                              >
                                <MessageSquare size={16} className="me-2" />
                                Adicionar Comentário
                              </CButton>
                            ) : (
                              <div className="comment-input-container">
                                <textarea
                                  className="form-control mb-3"
                                  rows="4"
                                  placeholder="Digite seu comentário aqui..."
                                  value={this.state.commentText}
                                  onChange={this.handleCommentChange}
                                />
                                <div className="d-flex justify-content-end gap-2">
                                  <CButton
                                    color="secondary"
                                    variant="outline"
                                    onClick={this.handleCancelComment}
                                  >
                                    Cancelar
                                  </CButton>
                                  <CButton
                                    color="primary"
                                    onClick={this.handleSaveComment}
                                  >
                                    Salvar
                                  </CButton>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </CCardBody>
                    </CCard>
                  </>
                )}
              </CCol>

              <CCol lg="6">
                <CCard className="h-100">
                  <CCardHeader>
                    <h5 className="mb-0">Visualização da Questão</h5>
                  </CCardHeader>
                  <CCardBody>
                    <ApplicatedQuestionVisualization
                      key={`${question.id}-${this.state.visualizationKey}`}
                      questionId={question.id}
                      questionOrder={1}
                      prototype
                      editable={false}
                      allowAnswerEdit={editMode}
                    />
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <CModal
          show={this.state.showDeleteModal}
          onClose={this.handleCancelDelete}
          color="danger"
          centered
        >
          <CModalHeader closeButton>
            <CModalTitle>Confirmar Exclusão</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <div className="p-3">
              Você tem certeza que gostaria de excluir este comentário? Esta
              ação não pode ser desfeita.
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={this.handleCancelDelete}>
              Cancelar
            </CButton>
            <CButton color="danger" onClick={this.handleConfirmDelete}>
              Excluir Comentário
            </CButton>
          </CModalFooter>
        </CModal>

        <ProtectedRoute routeId={this.props.currentRoute} />
      </>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
  general: state.general,
  profile: state.profile
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ logout, setObj }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(QuestionBankReviewEdit);
